import "./EstimateViewerMain.css";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";

import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton, Tooltip } from "@mui/material";

import apiKatsEstimate from "api/kats";
import apiProjects from "api/projects";
import apiProtocol from "api/protocol";
import apiVor from "api/vor";
import ContextMenu from "components/ContextMenu/ContextMenu.jsx";
import Footer from "components/Footer/Footer";
import RibbonContent from "components/Ribbon/RibbonContent/RibbonContent";
import { LocalStorageVarEnum, MaxFileSizeEnum } from "core/enums";
import { getEnvVal } from "core/env.js";
import { searchNestedIterableItem } from "core/iterable_utils";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useResizeHandlers } from "hooks/useResize";
import { ProjectTreeResizer } from "pages/EstimateViewer/components/ProjectTreeResizer/ProjectTreeResizer";
import { getTargetNmckId } from "pages/Nmck/NmckList/NmckList";
import ProjectAttrsModal from "pages/Projects/components/ProjectAttrsModal/ProjectAttrsModal";
import { setAnswer, setConfirmOptions } from "slices/confirm";
import { setContextMenuObj, setContextMenuPosition, setIsContextMenuOpen } from "slices/context_menu.js";
import { deleteCostSummary, getXmlCostSummary } from "slices/costSummary";
import { getPrintXlsxCostSummary } from "slices/costSummary/services/getPrintXlsxCostSummary";
import { applyColorToEstmTableRow, applyValidToProjectTreeRow, copyEstimateDocument } from "slices/estimateViewer/services";
import { setStashFiles } from "slices/import_files";
import {
	changeFiltredRowData as changeFiltredRowDataKats,
	deleteKats,
	getInputOriginXml as getInputOriginXmlKats,
	getInputOriginXmlWithJusts as getInputOriginXmlWithJustsKats,
	getInputOriginXmlWithJustsOcr as getInputOriginXmlWithJustsOcrKats,
	getKatsTree,
	getOutputXml as getOutputXmlKats,
	getOutputXmlWithJusts as getOutputXmlWithJustsKats,
	getPrintXlsx as getPrintXlsxKats,
	getPrintXlsxPpk,
	getPrintXlsxWithErrors as getPrintXlsxKatsWithErrors,
	setFiltredData as setTableDataKats,
	setKatsFetchStatus,
} from "slices/kats";
import {
	changeRowData as changeRowDataLocal,
	deleteLocalEstimate,
	getInputOriginXml as getInputOriginXmlLocal,
	getInputXml as getInputXmlLocal,
	getOutputXml as getOutputXmlLocal,
	getPrintXlsx as getPrintXlsxLocal,
	getPrintXlsxWithErrors as getPrintXlsxLocalWithErrors,
	setAdvSearchSelected,
	setLocalFetchStatus,
	setTableData as setTableDataLocal,
} from "slices/local_estimate";
import { setTableData as setEditorTableData } from "slices/local_estimate_editor";
import {
	changeRowData as changeRowDataObject,
	deleteObjectEstimate,
	getInputXml as getInputXmlObject,
	getPrintXlsx as getPrintXlsxObject,
	getPrintXlsxWithErrors as getPrintXlsxObjectWithErrors,
	setTableData as setTableDataObject,
} from "slices/object_estimate";
import {
	fetchProject,
	fetchProjectErrorsExcel,
	fetchProjectTree,
	forceFetchProjectTree,
	getProjectAttrsVisible,
	getProjectInputXml,
	getProjectMeta,
	getProjectSummaryTreeExpanded,
	getProjectSummaryTreeWidth,
	getProjectTree,
	makeProjectLsrDoubleWinner,
	projectWorkActions,
	setFetchStatus,
	setFetchStatusText,
	setSelected,
} from "slices/project_work";
import {
	changeRowData as changeRowDataProtocol,
	getProtocolExcel,
	getSelectedTypes,
	getUserSetAllowErPerc,
	getUserSetErrorStatus,
	getUserSetExcept,
	postUserSetGpr,
	setData as setDataProtocol,
	setReGetProtocol,
} from "slices/protocol";
import {
	setClickedTableClmName,
	setClickedTableRow,
	setCurrentEstmDocId,
	setCurrentProjectId,
	setLastActiveScroll,
	setLastEstimateCurrentPage,
	setPanelFoundRowPositionIndex,
	setPanelFoundRowPositions,
	setPanelSearchNumber,
	setPanelSearchPrevKey,
	setPanelSearchValue,
} from "slices/ribbon";
import {
	changeRowData as changeRowDataSum,
	deleteSummaryEstimate,
	setFetchStatus as setFetchSumStatus,
	setTableData as setTableDataSummary,
} from "slices/summary_estimate";
import { tutorialActions } from "slices/tutorial/tutorialSlice";
import { getUnionUserSettings, getUserSettings, getUserTasksAndRead, setUserTasksVisible } from "slices/user_adv";
import { deleteVor, getPrintXlsxProjectVor, getPrintXlsxVor, getXmlProjectVors, getXmlVor } from "slices/vor";
import { getVorHeaderData, getVorTableData } from "slices/vor/vorSelectors";
import { vorActions } from "slices/vor/vorSlice";

import AlertLoading, { selectFetchStatus } from "../../components/AlertLoading";
import ConfirmDialog from "../../components/ConfirmDialog";
import StartWorkBox from "../../components/StartWorkBox";
import KatsViewerFooterSettings from "../EstimateViewer/KatsViewer/KatsViewerFooterSettings";
import LocalEstimateViewerFooterSettings from "../EstimateViewer/LocalEstimateViewer/LocalEstimateViewerFooterSettings";
import ObjectEstimateViewerFooterSettings from "../EstimateViewer/ObjectEstimateViewer/ObjectEstimateViewerFooterSettings";
import SummaryEstimateViewerFooterSettings from "../EstimateViewer/SummaryEstimateViewer/SummaryEstimateViewerFooterSettings";
import AdvSearchBox from "./components/AdvSearchBox/AdvSearchBox";
import CommentsBoxModal from "./components/CommentsBox/CommentsBoxModal";
import CostSummaryViewer from "./CostSummaryViewer";
import EstimateRibbonContentWrapper from "./EstimateRibbonContentWrapper";
import EstimateRibbonSettings from "./EstimateRibbonSettings";
import { EstimateViewerContextMenuFactory, getContextMenuVisible } from "./EstimateViewerContextMenuFactory/EstimateViewerContextMenuFactory";
import GeneralEstmViewerFooterSettings from "./GeneralEstmViewerFooterSettings";
import KatsViewer from "./KatsViewer";
import { KatsPriceOfferSelectorModal } from "./KatsViewer/components/KatsPriceOfferSelectorModal/KatsPriceOfferSelectorModal";
import { UploadJustModel } from "./KatsViewer/components/UploadJustModel/UploadJustModel";
import { KatsProjectViewer } from "./KatsViewer/KatsProjectViewer";
import LocalEstimateViewer from "./LocalEstimateViewer";
import ObjectEstimateViewer from "./ObjectEstimateViewer";
import ProjectTree from "./ProjectTree/ProjectTree";
import ProtocolRibbonSettings from "./ProtocolRibbonSettings";
import ProtocolViewer from "./ProtocolViewer";
import RibbonPanelActionFactory from "./RibbonPanelActions/RibbonPanelActionFactory";
import SummaryEstimateViewer from "./SummaryEstimateViewer";
import VorViewer from "./VorViewer";

const SUMMARY_TREE_MIN_WIDTH = 100;
const PROJECT_TREE_ROW_HEIGHT = 20;
const HEADER_HEIGHT = 250;

export default function EstimateViewerMain() {
	const dispatch = useDispatch();
	const fetchStatus = useSelector((state) => state.projectWork.fetchStatus);
	const proj_main = useSelector((state) => state.projectWork.main, shallowEqual);
	const fetchMonStatus = useSelector((state) => state.projectWork.fetchMonStatus);
	const fetchStatusText = useSelector((state) => state.projectWork.fetchStatusText);
	const fetchTreeStatus = useSelector((state) => state.projectWork.fetchTreeStatus);
	// const fetchLsrStatus = useSelector((state) => state.localEstimate.fetchStatus);
	// const fetchKatsStatus = useSelector((state) => state.kats.fetchStatus);
	// const fetchSumStatus = useSelector((state) => state.summaryEstimate.fetchStatus);
	const treeData = useSelector(getProjectTree);
	const selected = useSelector((state) => state.projectWork.treeSelected, shallowEqual);
	const katsAllColumnsSettings = useSelector((state) => state.kats.columnsSettings);
	const katsHeaderData = useSelector((state) => state.kats.headerData);
	const [katsColumnsSettings, setKatsColumnsSettings] = useState([]);
	const lsrData = useSelector((state) => state.localEstimate.data);

	const currentEstmDocId = useSelector((state) => state.ribbon.currentEstmDocId);
	const currentPage = useSelector((state) => state.ribbon.currentPage);
	const currentEstmDocUrlPart = useSelector((state) => state.ribbon.currentEstmDocUrlPart);
	const clickedTableCtrlRows = useSelector((state) => state.ribbon.clickedTableCtrlRows);
	const clickedTableRow = useSelector((state) => state.ribbon.clickedTableRow);
	const panelSearchNumber = useSelector((state) => state.ribbon.panelSearchNumber);
	const panelSearchPrevKey = useSelector((state) => state.ribbon.panelSearchPrevKey);
	const panelSearchValue = useSelector((state) => state.ribbon.panelSearchValue);
	const panelFoundRowPositions = useSelector((state) => state.ribbon.panelFoundRowPositions);
	const panelFoundRowPositionIndex = useSelector((state) => state.ribbon.panelFoundRowPositionIndex);
	const isContextMenuOpen = useSelector((state) => state.contextMenu.isContextMenuOpen);
	const contextMenuPosition = useSelector((state) => state.contextMenu.contextMenuPosition);
	const contextMenuObj = useSelector((state) => state.contextMenu.contextMenuObj);

	const confirmVisible = useSelector((state) => state.confirm.visible, shallowEqual);
	const windowWidth = useSelector((state) => state.windowDimensions.windowWidth);
	const userSettings = useSelector(getUnionUserSettings);

	const [advSearchBoxVisible, setAdvSearchBoxVisible] = useState(false);
	const [ksrVisible, setKsrVisible] = useState(false);
	const [fsnbVisible, setFsnbVisible] = useState(false);
	const [advSearchBoxMode, setAdvSearchBoxMode] = useState("");
	const [selectedErrorStatus, setSelectedErrorStatus] = useState([]);
	const [katsPriceOfferSelectorVisible, setKatsPriceOfferSelectorVisible] = useState(false);
	const [commentBoxVisible, setCommentBoxVisible] = useState(false);
	const [commentBoxObj, setCommentBoxObj] = useState();
	const [isDraged, setIsDraged] = useState("leave");

	const userPermissions = useSelector((state) => state.auth.permissions);

	const selectTypeData = useSelector((state) => state.protocol.selectTypeData);
	const gprVisible = useSelector((state) => state.protocol.gprVisible);
	const [allowErPercVal, setAllowErPercVal] = useState(0);
	const allowErPerc = useSelector((state) => state.protocol.allowErPerc);
	const localEstmData = useSelector((state) => state.localEstimate.data);
	const localEstimateTableData = useSelector((state) => state.localEstimate.tableData);
	const objectEstimateTableData = useSelector((state) => state.objectEstimate.tableData);
	const objectEstimateHeaderData = useSelector((state) => state.objectEstimate.headerData);
	const summaryEstimateTableData = useSelector((state) => state.summaryEstimate.tableData);
	const katsEstimateTableData = useSelector((state) => state.kats.filtredData);
	const vorTableData = useSelector(getVorTableData);
	const protocolTableData = useSelector((state) => state.protocol.data);
	const summaryHeaderData = useSelector((state) => state.summaryEstimate.headerData);
	const vorHeaderData = useSelector(getVorHeaderData);
	const summaryTreeWidth = useSelector(getProjectSummaryTreeWidth);
	const setSummaryTreeWidth = (newValue) => dispatch(projectWorkActions.setSummaryTreeWidth(newValue));
	const summaryTreeExpanded = useSelector(getProjectSummaryTreeExpanded);
	const setSummaryTreeExpanded = (newValue) => dispatch(projectWorkActions.setSummaryTreeExpanded(newValue));
	const ssrHeaderData = useSelector((state) => state.summaryEstimate.headerData);
	const projectMeta = useSelector(getProjectMeta);

	const projectAttrsVisible = useSelector(getProjectAttrsVisible);

	const scrollBlock = useRef();
	const treeScrollBlock = useRef();
	const projectTreeRef = useRef();

	const lowerCallbacks = useRef({ local: {}, object: {}, protocol: {}, summary: {}, kats: {}, project: {} });

	const navigate = useNavigate();
	const { id } = useParams();

	const location = useLocation();
	const { getLsItem, removeLsItem } = useLocalStorage();

	const localFetchStatus = selectFetchStatus([
		fetchStatus,
		fetchTreeStatus,
		fetchMonStatus,
		// fetchSumStatus,
		// fetchKatsStatus,
		// fetchLsrStatus,
	]);

	const [deletableSelected, setDeletableSelected] = useState(0);

	const [errorsVisible, setErrorsVisible] = useState(null);
	const [expectPopVisb, setExpectPopVisb] = useState(false);
	const [katsJustUploadVisible, setKatsJustUploadVisible] = useState(false);
	const [katsJustUploadCnt, setKatsJustUploadCnt] = useState(0);

	const lsrId = parseInt(location.pathname.slice(location.pathname.indexOf("lsr/") + 4));
	const osrId = parseInt(location.pathname.slice(location.pathname.indexOf("osr/") + 4));
	const katsId = parseInt(location.pathname.slice(location.pathname.indexOf("kats/") + 5));
	const vorId = parseInt(location.pathname.slice(location.pathname.indexOf("vor/") + 4));
	const costSummaryId = parseInt(location.pathname.slice(location.pathname.indexOf("cost_summary/") + 13));

	const treeDataHasKatsObjs = useMemo(() => {
		return treeData?.find((x) => x.type === "kats" && x._children && x._children.length && x._children.length > 0);
	}, [treeData, id]); // eslint-disable-line react-hooks/exhaustive-deps

	const clearPanel = () => {
		dispatch(setPanelSearchNumber(""));
		dispatch(setPanelSearchPrevKey(""));
		dispatch(setPanelFoundRowPositionIndex(0));
		dispatch(setPanelSearchValue(""));
		dispatch(setPanelFoundRowPositions([]));
		dispatch(setClickedTableClmName(""));
	};

	useEffect(() => {
		dispatch(setLastEstimateCurrentPage(currentPage));
		clearPanel();
	}, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

	useLayoutEffect(() => {
		if (getLsItem({ key: LocalStorageVarEnum.ESTM_VIEWER_ADV_SEACH_SHOW })) {
			setAdvSearchBoxVisible(true);
			setAdvSearchBoxMode("search");
			removeLsItem({ key: LocalStorageVarEnum.ESTM_VIEWER_ADV_SEACH_SHOW });
		}

		return () => {
			dispatch(setLastActiveScroll(scrollBlock?.current?.scrollTop)); // eslint-disable-line react-hooks/exhaustive-deps
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	/*eslint-disable */
	useEffect(() => {
		dispatch(tutorialActions.setPage("estimate_viewer"));
		dispatch(setFetchStatus("success"));
		dispatch(setFetchSumStatus("success"));
		dispatch(setCurrentProjectId(id));
		dispatch(setEditorTableData([]));

		dispatch(fetchProject(id));
		dispatch(fetchProjectTree(id)).then((data) => {
			if (data?.meta?.requestStatus == "rejected") {
				navigate("/");
				return;
			}
			if (!data.payload) return;
			// Для выбора документа в дереве при прямом открытии страницы по ссылке
			let seekKey;
			if (lsrId) {
				seekKey = `local-${lsrId}`;
			} else if (osrId) {
				seekKey = `object-${osrId}`;
			} else if (katsId) {
				seekKey = `kats-${katsId}`;
			} else if (vorId) {
				seekKey = `vor-${vorId}`;
			} else if (costSummaryId) {
				seekKey = `cost_summary-${costSummaryId}`;
			}
			if (seekKey) {
				const treeData = data?.payload?.data == null ? data.payload : data.payload.data;

				const { seekItem, seekCount, isFound } = searchNestedIterableItem({
					iterable: treeData,
					isSeekRowCallback: (row) => row.key === seekKey,
					isPermittedRowCallback: () => true,
					isPermittedChildsLoopCallback: () => true,
					breakAfterFirstMatch: true,
				});

				dispatch(setSelected(seekItem));
				if (treeScrollBlock?.current && isFound) {
					const ROWS_COUNT_PUT_DOWN = 4;
					const newScrollTop = PROJECT_TREE_ROW_HEIGHT * (seekCount - ROWS_COUNT_PUT_DOWN);
					if (newScrollTop > window.innerHeight - HEADER_HEIGHT && treeScrollBlock.current?.scrollTop === 0) {
						treeScrollBlock.current.scrollTop = newScrollTop;
					}
				}
			}
		});
		clearPanel();
	}, [id, lsrId, osrId, katsId, vorId, costSummaryId]);

	useEffect(() => {
		if (proj_main["id"] != null) {
			let body = {
				project_id: proj_main["id"],
			};
			dispatch(getUserSetErrorStatus(body));
			dispatch(getSelectedTypes(id));
			dispatch(getUserSetExcept(body));
			dispatch(getUserSetAllowErPerc(body));
			dispatch(getUserSettings());
		}

		return () => {
			// dispatch(setAdvSearchSelected([]));
			clearPanel();
		};
	}, [proj_main]);

	useEffect(() => {
		if (advSearchBoxVisible === false) {
			dispatch(setAdvSearchSelected([]));
		}
	}, [advSearchBoxVisible]);

	useEffect(() => {
		setAllowErPercVal(allowErPerc);
	}, [allowErPerc]);

	useEffect(() => {
		setDeletableSelected(
			selected.filter(
				(x) => ["local", "object", "kats", "vor", "cost_summary", "summary"].includes(x["type"]) === true && x["id"] !== 0 && x["id"] != null
			).length
		);
	}, [selected]);
	/*eslint-enable */

	useEffect(() => {
		if (panelSearchNumber && panelSearchNumber !== "") {
			RibbonPanelActionFactory({
				action: "MOVE_TO_NUMBER",
				currentPage,
				arg: panelSearchNumber,
				tableData: {
					local: localEstimateTableData,
					object: objectEstimateTableData,
					summary: summaryEstimateTableData,
					kats: katsEstimateTableData,
					vor: vorTableData,
					protocol: protocolTableData,
				},
				updateTableData: {
					local: changeRowDataLocal,
					object: changeRowDataObject,
					summary: changeRowDataSum,
					kats: changeFiltredRowDataKats,
					vor: vorActions.changeRowData,
					protocol: changeRowDataProtocol,
				},
				scrollBlock,
				dispatch,
				panelSearchPrevKey,
			});
		}
	}, [panelSearchNumber]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (panelSearchValue && panelSearchValue !== "") {
			RibbonPanelActionFactory({
				action: "SEARCH_INPUT",
				currentPage,
				arg: panelSearchValue,
				tableData: {
					local: localEstimateTableData,
					object: objectEstimateTableData,
					summary: summaryEstimateTableData,
					kats: katsEstimateTableData,
					vor: vorTableData,
					protocol: protocolTableData,
				},
				updateTableData: {
					local: setTableDataLocal,
					object: setTableDataObject,
					summary: setTableDataSummary,
					kats: setTableDataKats,
					vor: vorActions.setTableData,
					protocol: setDataProtocol,
				},
				scrollBlock,
				dispatch,
				panelSearchPrevKey,
			});
		}
	}, [panelSearchValue]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		dispatch(setPanelFoundRowPositionIndex(0));
	}, [panelFoundRowPositions]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		RibbonPanelActionFactory({
			action: "MOVE_TO_INDEX",
			currentPage: currentPage,
			arg: panelFoundRowPositions[panelFoundRowPositionIndex],
			scrollBlock: scrollBlock,
		});
	}, [panelFoundRowPositionIndex, panelFoundRowPositions]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleEnterParentSearchPress = () => {
		if (panelFoundRowPositions.length === 0) {
			dispatch(setPanelFoundRowPositionIndex(0));
		} else {
			dispatch(setPanelFoundRowPositionIndex((panelFoundRowPositionIndex + 1) % panelFoundRowPositions.length));
		}
	};

	const showDeleteDialog = ({ cascade, priceLevel }) => {
		let text = "Удалить выбранный документ?";
		if (selected.length > 1) {
			text = "Удалить выбранные документы?";
		}
		if (deletableSelected) {
			dispatch(
				setConfirmOptions({
					label: "Удаление",
					text: text,
					labelCancel: "Закрыть",
					labelOk: "Удалить",
					visible: true,
					callback: () => deleteSeleted({ cascade, priceLevel }),
				})
			);
		}
	};

	const deleteSeleted = ({ cascade, priceLevel }) => {
		dispatch(setAnswer(false));
		if (deletableSelected) {
			for (let obj of selected) {
				if (obj["type"] === "object") {
					const prom = dispatch(deleteObjectEstimate({ estmId: obj["id"], cascade: cascade }));
					prom.then(({ meta }) => {
						if (meta.requestStatus === "rejected") throw new Error();
						const tProm = dispatch(forceFetchProjectTree(id));
						tProm.then(({ meta }) => {
							dispatch(setCurrentEstmDocId(false));
							navigate("/project/" + obj["project_id"]);
						});
					}).catch((err) => {});
				} else if (obj["type"] === "local") {
					const prom = dispatch(deleteLocalEstimate(obj["id"]));
					prom.then(({ meta }) => {
						if (meta.requestStatus === "rejected") throw new Error();
						const tProm = dispatch(fetchProjectTree(id));
						tProm.then(() => {
							dispatch(setCurrentEstmDocId(false));
							navigate("/project/" + obj["project_id"]);
						});
					}).catch((err) => {});
				} else if (obj["type"] === "kats") {
					const prom = dispatch(deleteKats(obj["id"]));
					prom.then(({ meta }) => {
						if (meta.requestStatus === "rejected") throw new Error();
						return dispatch(fetchProjectTree(id));
					})
						.then((data) => {
							dispatch(setCurrentEstmDocId(false));
							navigate("/project/" + obj["project_id"]);
						})
						.catch((err) => {});
				} else if (obj["type"] === "vor") {
					const prom = dispatch(deleteVor({ vorId: obj["id"] }));
					prom.then((res) => {
						if (res?.error?.message === "Rejected") throw new Error();
						return dispatch(fetchProjectTree(id));
					})
						.then((data) => {
							dispatch(setCurrentEstmDocId(false));
							navigate("/project/" + obj["project_id"]);
						})
						.catch((err) => {});
				} else if (obj["type"] === "cost_summary") {
					const prom = dispatch(deleteCostSummary({ costSummaryId: obj["id"] }));
					prom.then((res) => {
						if (res?.error?.message === "Rejected") throw new Error();
						return dispatch(fetchProjectTree(id));
					})
						.then((data) => {
							dispatch(setCurrentEstmDocId(false));
							navigate("/project/" + obj["project_id"]);
						})
						.catch((err) => {});
				} else if (obj["type"] === "summary") {
					const prom = dispatch(deleteSummaryEstimate({ id: obj["project_id"], priceLevel }));
					prom.then((res) => {
						if (res?.error?.message === "Rejected") {
							throw new Error();
						} else {
							return dispatch(fetchProjectTree(id));
						}
					})
						.then((data) => {
							dispatch(setCurrentEstmDocId(false));
							navigate("/project/" + obj["project_id"] + "/start");
						})
						.catch((err) => {});
				}
			}
		}
	};

	const showErrorDeleteDialog = () => {
		if (clickedTableRow.key) {
			dispatch(
				setConfirmOptions({
					label: "Удаление ошибки",
					// eslint-disable-next-line
					text: "Внимание! Удаление ошибки из протокола необратимая операции. \
						Для восстановления полной редакции протокола (с ранее удаленными ошибками) необходимо будет перезапустить алгоритм проверки. \
						Вы уверены, что хотите удалить выделенную ошибку?",
					labelCancel: "Закрыть",
					labelOk: "Удалить",
					visible: true,
					callback: () => deleteProtocolError(),
				})
			);
		}
	};

	const showDedublicateDialog = ({ callback }) => {
		dispatch(
			setConfirmOptions({
				label: "Внимание!",
				text: "После выполнения данной операции необходимо перезапустить процедуру Перекрестной проверки (Этап 3), в целях актуализации данных протокола проверок.",
				labelCancel: "Закрыть",
				labelOk: "ОК",
				visible: true,
				styles: { width: "600px" },
				callback: () => callback(),
			})
		);
	};

	const deleteProtocolError = () => {
		const prom = apiProtocol.deleteProtocolError({ projectId: id, estmType: currentEstmDocUrlPart, errorId: clickedTableRow?.id });
		prom.then(() => dispatch(setReGetProtocol(true)));
	};

	const onClickSmetBtn = () => {
		let newUrl = "";
		if (!["ssr", ""].includes(currentEstmDocUrlPart)) {
			newUrl = `/project/${id}/${currentEstmDocUrlPart}/${currentEstmDocId}/`;
		} else {
			newUrl = `/project/${id}/`;
		}
		navigate(newUrl);
		dispatch(setClickedTableRow(null));
	};

	const onClickProtocolBtn = () => {
		let newUrl = "";
		if (!["SummaryEstimateViewer", "StartEstimateViewer", "KatsProjectEstimateViewer"].includes(currentPage) && currentEstmDocId != null) {
			newUrl = `/project/${id}/${currentEstmDocUrlPart}/${currentEstmDocId}/protocol/`;
		} else if (currentPage === "KatsProjectEstimateViewer") {
			newUrl = `/project/${id}/kats/`;
		} else {
			newUrl = `/project/${id}/protocol/`;
		}
		navigate(newUrl);
		dispatch(setClickedTableRow(null));
	};

	const onClickMonitorBtn = () => {
		navigate("/project/" + id + "/monitor/");
		dispatch(setClickedTableRow(null));
	};

	const onClickImportBtn = () => {
		navigate("/project/" + id + "/import/");
		dispatch(setClickedTableRow(null));
	};

	const onClickExportSmetBtn = () => {
		dispatch(fetchProjectErrorsExcel(id));
	};

	const onClickExportCurrentSmetBtn = () => {
		if (currentEstmDocId) dispatch(getProtocolExcel({ type: currentEstmDocUrlPart, id: currentEstmDocId }));
	};

	const onClickPrintCurrentSmetBtn = (withErrors) => {
		if (selected.length > 1) {
			const body = [...selected].map((elm) => {
				return { id: elm.id, type: elm.type };
			});
			let prom;
			if (withErrors) {
				prom = apiProjects.getPartProjectPrintXlsxWithErrors(id, body);
			} else {
				prom = apiProjects.getPartProjectPrintXlsx(id, body);
			}
			prom.then((response) => {
				dispatch(getUserTasksAndRead());
				dispatch(setUserTasksVisible(true));
			});
			return;
		}

		if (currentEstmDocId) {
			if (withErrors) {
				if (currentPage === "LocalEstimateViewer") {
					dispatch(getPrintXlsxLocalWithErrors(currentEstmDocId));
				} else if (currentPage === "KatsEstimateViewer") {
					dispatch(getPrintXlsxKatsWithErrors(currentEstmDocId));
				} else if (currentPage === "ObjectEstimateViewer") {
					dispatch(getPrintXlsxObjectWithErrors(currentEstmDocId));
				} else if (currentPage === "SummaryEstimateViewer") {
					lowerCallbacks.current.summary["getPrintXlsxWithErrorsSummary"]();
				}
			} else {
				if (currentPage === "LocalEstimateViewer") {
					dispatch(getPrintXlsxLocal(currentEstmDocId));
				} else if (currentPage === "ObjectEstimateViewer") {
					dispatch(getPrintXlsxObject(currentEstmDocId));
				} else if (currentPage === "KatsEstimateViewer") {
					dispatch(getPrintXlsxKats(currentEstmDocId));
				} else if (currentPage === "VorViewer") {
					dispatch(getPrintXlsxVor({ vorId: currentEstmDocId }));
				} else if (currentPage === "CostSummaryViewer") {
					dispatch(getPrintXlsxCostSummary({ costSummaryId: currentEstmDocId }));
				} else if (currentPage === "SummaryEstimateViewer") {
					lowerCallbacks.current.summary["getPrintXlsxSummary"]();
				}
			}
		} else {
			if (currentPage === "VorViewer") {
				dispatch(getPrintXlsxProjectVor({ projectId: proj_main["id"] }));
			}
		}
	};

	const onClickPrintXlsxPpk = () => {
		dispatch(getPrintXlsxPpk(katsId));
	};

	const onClickPrintFullSmetBtn = (withErrors) => {
		let prom;
		if (withErrors) {
			prom = apiProjects.getProjectPrintXlsxWithErrors(id);
		} else {
			prom = apiProjects.getProjectPrintXlsx(id);
		}
		prom.then((response) => {
			dispatch(getUserTasksAndRead());
			dispatch(setUserTasksVisible(true));
		});
	};

	const onClickInputXmlFullSmetBtn = () => {
		dispatch(getProjectInputXml(id));
	};

	const onClickCurrentXmlExportBtn = (param) => {
		if (getEnvVal("App") === "sipcs" && currentPage === "LocalEstimateViewer") {
			dispatch(getOutputXmlLocal(currentEstmDocId));
		} else {
			if (currentPage === "LocalEstimateViewer") {
				dispatch(getInputXmlLocal(currentEstmDocId));
			} else if (currentPage === "ObjectEstimateViewer") {
				dispatch(getInputXmlObject(currentEstmDocId));
			} else if (currentPage === "KatsEstimateViewer") {
				if (param === "WITH_JUSTS") {
					dispatch(getOutputXmlWithJustsKats(currentEstmDocId));
				} else {
					dispatch(getOutputXmlKats(currentEstmDocId));
				}
			} else if (currentPage === "SummaryEstimateViewer") {
				lowerCallbacks.current.summary["getInputXmlSummary"]();
			} else if (currentPage === "VorViewer") {
				if (param === "ALL_VOR_ONLY") {
					dispatch(getXmlProjectVors({ projectId: proj_main["id"], params: { source: "CREATE", withJust: param === "WITH_JUSTS" } }));
				} else if (vorId) {
					dispatch(getXmlVor({ vorId, params: { source: "CREATE", withJust: param === "WITH_JUSTS" } }));
				} else {
					dispatch(getXmlProjectVors({ projectId: proj_main["id"], params: { source: "CREATE", withJust: param === "WITH_JUSTS" } }));
				}
			} else if (currentPage === "CostSummaryViewer") {
				dispatch(getXmlCostSummary({ costSummaryId }));
			}
		}
	};

	const getInputOriginXml = (param) => {
		if (currentPage === "LocalEstimateViewer") {
			dispatch(getInputOriginXmlLocal(currentEstmDocId));
		} else if (currentPage === "KatsEstimateViewer") {
			if (param === "WITH_JUSTS") {
				dispatch(getInputOriginXmlWithJustsKats(currentEstmDocId));
			} else if (param === "WITH_JUSTS_OCR") {
				dispatch(getInputOriginXmlWithJustsOcrKats(currentEstmDocId));
			} else {
				dispatch(getInputOriginXmlKats(currentEstmDocId));
			}
		} else if (currentPage === "VorViewer") {
			dispatch(getXmlVor({ vorId, params: { source: "SOURCE", withJust: param === "WITH_JUSTS" } }));
		} else if (currentPage === "CostSummaryViewer") {
			dispatch(getXmlCostSummary({ costSummaryId }));
		}
	};

	const onClickSearchBtn = () => {
		if (advSearchBoxMode !== "rating") {
			setAdvSearchBoxVisible((prev) => !prev);
		} else {
			setAdvSearchBoxVisible(true);
		}
		setAdvSearchBoxMode("search");
	};

	const onClickSortBtn = () => {
		if (advSearchBoxMode !== "search") {
			setAdvSearchBoxVisible((prev) => !prev);
		} else {
			setAdvSearchBoxVisible(true);
		}
		setAdvSearchBoxMode("rating");
	};

	const onClickChangeRowValueBtn = ({ key, value }) => {
		if (currentPage === "LocalEstimateViewer") {
			if (clickedTableRow) {
				dispatch(
					changeRowDataLocal({
						rowKey: clickedTableRow.key,
						callback: (row) => (row[key] = value),
					})
				);
				dispatch(setClickedTableRow({ ...clickedTableRow, [key]: value }));
			}
		}
	};

	const saveFiltredDataGrp = () => {
		const body = [];
		for (let step of selectTypeData) {
			for (let group of step._children) {
				for (let type of group._children) {
					if (type.checked) {
						body.push({ step_id: type.step_id, group_id: type.group_id, type_id: type.id });
					}
				}
			}
		}
		dispatch(
			postUserSetGpr({
				project_id: proj_main["id"],
				body: body,
			})
		);
	}; // eslint-disable-line react-hooks/exhaustive-deps

	const uploadJustFiles = async ({ files }) => {
		dispatch(setFetchStatus("loading"));
		let api;
		if (currentPage === "KatsEstimateViewer") {
			api = apiKatsEstimate;
		} else if (currentPage === "VorViewer") {
			api = apiVor;
		}
		if (!api) return;
		const promiseArr = [];
		for (let file of files) {
			if (file?.size > MaxFileSizeEnum.KATS_JUST) {
				dispatch(setFetchStatus("failed"));
				dispatch(setFetchStatusText("Размер файла превышает допустимый"));
				return;
			}
			promiseArr.push(api.uploadJust({ file, estmId: currentEstmDocId }));
		}
		Promise.all(promiseArr)
			.then(() => {
				dispatch(setFetchStatus("successAlert"));
				dispatch(setFetchStatusText("Обоснования загружены"));
			})
			.catch(() => {
				dispatch(setFetchStatus("failed"));
			});

		setTimeout(() => {
			setKatsJustUploadCnt((prev) => (prev += 1));
			if (currentPage === "KatsEstimateViewer") dispatch(getKatsTree(currentEstmDocId));
		}, 500);
		setTimeout(() => {
			dispatch(setFetchStatus("success"));
		}, 5000);
	};

	const fillColorTableRow = (color) => {
		dispatch(applyColorToEstmTableRow({ color }));
	};

	const showProjectTreeComment = ({ targetRow }) => {
		const selectedProjectTreeRow = selected[0];
		if (!selectedProjectTreeRow && !targetRow) return;

		if (targetRow) {
			setCommentBoxObj(targetRow);
		} else {
			setCommentBoxObj(selectedProjectTreeRow);
		}
		setCommentBoxVisible(true);
	};

	const onClickShowKsr = () => {
		setKsrVisible((prev) => !prev);
	};

	const onClickShowFsnb = () => {
		setFsnbVisible((prev) => !prev);
	};

	lowerCallbacks.current.project["showProjectTreeComment"] = showProjectTreeComment;

	const openContextMenu = ({ clickedRow, event }) => {
		event.preventDefault();
		dispatch(setContextMenuPosition({ event }));
		dispatch(setContextMenuObj({ ...clickedRow, model_name: "ProjectTreeDoc" }));
		dispatch(setIsContextMenuOpen(true));
	};

	const copyEstimateDoc = () => dispatch(copyEstimateDocument());

	const {
		toggleHandler: summaryTreeToggleHandler,
		saveWidthHandler: saveSummaryTreeWidth,
		initialWidthHandler: getInitialSummaryTreeWidth,
	} = useResizeHandlers({
		resizableElem: projectTreeRef.current,
		expanded: summaryTreeExpanded,
		setExpanded: setSummaryTreeExpanded,
		minWidth: SUMMARY_TREE_MIN_WIDTH,
		width: summaryTreeWidth,
		setWidth: setSummaryTreeWidth,
		settingsPageName: "estimateViewer",
		settingExpandedKey: "summaryTreeExpanded",
		settingWidthKey: "summaryTreeWidth",
	});

	if (proj_main.id === null) {
		return null;
	}

	const showClearProjectDialog = () => {
		dispatch(
			setConfirmOptions({
				label: "Очистить проект?",
				text: "Операция очистки приведет к удалению всех загруженных сметных документов проекта",
				labelCancel: "Закрыть",
				labelOk: "Удалить",
				visible: true,
				callback: () => clearProject(id),
			})
		);
	};

	const clearProject = (projectId) => {
		dispatch(setFetchStatus("loading"));
		const prom = apiProjects.clearProject(projectId);
		prom.then((response) => {
			dispatch(setFetchStatus("success"));
			dispatch(fetchProjectTree(projectId));
		});
	};

	const makeLsrDoubleWinner = () => {
		const prom = dispatch(
			makeProjectLsrDoubleWinner({
				projectId: proj_main.id,
				groupId: selected?.[0]?.double_group_id,
				lsrId: selected?.[0]?.id,
			})
		);
		prom.then(() => dispatch(setReGetProtocol(true)));
	};

	const hasFooter = () => {
		if (selected?.[0]?.id == null) return false;
		return [
			"KatsEstimateViewer",
			"LocalEstimateViewer",
			"SummaryEstimateViewer",
			"ObjectEstimateViewer",
			// "KatsProjectEstimateViewer",
			"CostSummaryViewer",
			"VorViewer",
		].includes(currentPage);
	};

	const preventEvent = ({ event }) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const handleDrag = ({ event, dragAction }) => {
		event.preventDefault();
		event.stopPropagation();
		setIsDraged(dragAction);
	};

	const handleDrop = ({ event }) => {
		handleDrag({ event, dragAction: "leave" });
		dispatch(setStashFiles(event.dataTransfer.files));
		navigate(`/project/${proj_main["id"]}/import/`);
	};

	const goToNewNmck = () => navigate(`/project/${id}/nmck/new/`);

	const getNmckLink = () => {
		const targetNmckId = getTargetNmckId(proj_main.nmck_list);

		return targetNmckId ? () => navigate(`/project/${id}/nmck/${targetNmckId}/`) : undefined;
	};

	const goToLsrVorConnectionPage = () => navigate(`/project/${id}/lsr_vor_connection/`);

	const showNmckButton = userPermissions.is_nmck_user === true;

	return (
		<>
			<RibbonContent />
			<EstimateRibbonContentWrapper
				EstimateRibbonSettings={EstimateRibbonSettings({
					dispatch: dispatch,
					selectTypeData: selectTypeData,
					gprVisible: gprVisible,
					saveFiltredDataGrp: saveFiltredDataGrp,
					proj_main: proj_main,
					selectedErrorStatus: selectedErrorStatus,
					setSelectedErrorStatus: setSelectedErrorStatus,
					onClickProtocolBtn: onClickProtocolBtn,
					onClickMonitorBtn: onClickMonitorBtn,
					onClickImportBtn: onClickImportBtn,
					onClickExportSmetBtn: onClickExportSmetBtn,
					onClickExportCurrentSmetBtn: onClickExportCurrentSmetBtn,
					currentEstmDocId: currentEstmDocId,
					onClickPrintCurrentSmetBtn: onClickPrintCurrentSmetBtn,
					onClickPrintFullSmetBtn: onClickPrintFullSmetBtn,
					onClickCurrentXmlExportBtn: onClickCurrentXmlExportBtn,
					onClickInputXmlFullSmetBtn: onClickInputXmlFullSmetBtn,
					onClickSearchBtn: onClickSearchBtn,
					onClickSortBtn: onClickSortBtn,
					deletableSelected: deletableSelected,
					showDeleteDialog: showDeleteDialog,
					setErrorsVisible: setErrorsVisible,
					windowWidth: windowWidth,
					currentPage: currentPage,
					clickedTableRow: clickedTableRow,
					onClickChangeRowValueBtn: onClickChangeRowValueBtn,
					userPermissions: userPermissions,
					lowerCallbacks: lowerCallbacks,
					advSearchBoxMode: advSearchBoxMode,
					advSearchBoxVisible: advSearchBoxVisible,
					allowErPercVal: allowErPercVal,
					setAllowErPercVal: setAllowErPercVal,
					navigate: navigate,
					localEstmData: localEstmData,
					setKatsJustUploadVisible: setKatsJustUploadVisible,
					setKatsPriceOfferSelectorVisible: setKatsPriceOfferSelectorVisible,
					fillColorTableRow: fillColorTableRow,
					projectTreeSelected: selected,
					getInputOriginXml: getInputOriginXml,
					allColumnsSettings: { kats: katsAllColumnsSettings },
					columnsSettings: { kats: katsColumnsSettings },
					setColumnsSettings: { kats: setKatsColumnsSettings },
					userSettings: userSettings,
					ksrVisible: ksrVisible,
					onClickShowKsr: onClickShowKsr,
					onClickPrintXlsxPpk: onClickPrintXlsxPpk,
					fsnbVisible: fsnbVisible,
					showClearProjectDialog: showClearProjectDialog,
					currentEstmDocUrlPart: currentEstmDocUrlPart,
					makeLsrDoubleWinner: makeLsrDoubleWinner,
					showDedublicateDialog: showDedublicateDialog,
					ssrHeaderData: ssrHeaderData,
					goToNewNmck,
					goToNmckItem: getNmckLink(),
					showNmckButton,
					vorHeaderData,
					copyEstimateDoc,
					goToLsrVorConnectionPage,
				})}
				ProtocolRibbonSettings={ProtocolRibbonSettings({
					dispatch: dispatch,
					selectTypeData: selectTypeData,
					gprVisible: gprVisible,
					saveFiltredDataGrp: saveFiltredDataGrp,
					proj_main: proj_main,
					selectedErrorStatus: selectedErrorStatus,
					setSelectedErrorStatus: setSelectedErrorStatus,
					onClickSmetBtn: onClickSmetBtn,
					onClickMonitorBtn: onClickMonitorBtn,
					onClickImportBtn: onClickImportBtn,
					onClickExportSmetBtn: onClickExportSmetBtn,
					onClickExportCurrentSmetBtn: onClickExportCurrentSmetBtn,
					onClickPrintCurrentSmetBtn: onClickPrintCurrentSmetBtn,
					onClickPrintFullSmetBtn: onClickPrintFullSmetBtn,
					currentEstmDocId: currentEstmDocId,
					expectPopVisb: expectPopVisb,
					setExpectPopVisb: setExpectPopVisb,
					allowErPercVal: allowErPercVal,
					setAllowErPercVal: setAllowErPercVal,
					lowerCallbacks: lowerCallbacks,
					clickedTableRow: clickedTableRow,
					currentPage: currentPage,
					userPermissions: userPermissions,
					navigate: navigate,
					localEstmData: localEstmData,
					setKatsJustUploadVisible: setKatsJustUploadVisible,
					currentEstmDocUrlPart: currentEstmDocUrlPart,
					showDeleteDialog: showDeleteDialog,
					showErrorDeleteDialog: showErrorDeleteDialog,
					fillColorTableRow: fillColorTableRow,
					userSettings: userSettings,
					projectTreeSelected: selected,
				})}
				handleEnterParentSearchPress={handleEnterParentSearchPress}
			/>
			<div
				className="ProjectSummaryDnD"
				is_draged={isDraged}
				onDragEnter={(event) => preventEvent({ event })}
				onDragOver={(event) => preventEvent({ event })}
				onDragLeave={(event) => handleDrag({ event, dragAction: "leave" })}
				onDrop={(event) => handleDrop({ event })}
			></div>
			<div
				className={hasFooter() ? "ProjectSummary ProjectSummaryWithFooter" : "ProjectSummary"}
				onDragEnter={(event) => handleDrag({ event, dragAction: "enter" })}
				onDragOver={(event) => handleDrag({ event, dragAction: "enter" })}
				onDragLeave={(event) => preventEvent({ event })}
				onDrop={(event) => preventEvent({ event })}
			>
				<div className={hasFooter() ? "ProjectSummaryTree ProjectSummaryTreeWithFooter" : "ProjectSummaryTree"} ref={treeScrollBlock}>
					<div ref={projectTreeRef} className="ProjectSummaryTreeFixed" style={{ width: getInitialSummaryTreeWidth() }}>
						<div className="ProjectSummaryName">
							<div
								className="ProjectSummaryNameWrapper"
								style={{
									display: "-webkit-box",
									WebkitLineClamp: 3,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
									userSelect: "text",
								}}
							>
								{proj_main.name}
							</div>
							<div>
								{advSearchBoxVisible && (
									<Tooltip
										title="Закрыть"
										onClick={() => {
											setAdvSearchBoxVisible(false);
										}}
									>
										<IconButton size="small" sx={{ padding: "2px", marginRight: "2px" }}>
											<ClearIcon sx={{ fontSize: "17px" }} />
										</IconButton>
									</Tooltip>
								)}
								<Tooltip title={summaryTreeExpanded ? "Свернуть" : "Развернуть"} onClick={summaryTreeToggleHandler}>
									<IconButton size="small" sx={{ padding: "2px" }}>
										{summaryTreeExpanded ? <ChevronLeft sx={{ fontSize: "17px" }} /> : <ChevronRight sx={{ fontSize: "17px" }} />}
									</IconButton>
								</Tooltip>
							</div>
						</div>
						<AdvSearchBox
							visible={advSearchBoxVisible}
							mode={advSearchBoxMode}
							setAdvSearchBoxVisible={setAdvSearchBoxVisible}
							treeScrollBlock={treeScrollBlock}
						/>
						<div className="ProjectEstmateTree">
							{treeData.length > 0 ? (
								<ProjectTree
									callbacks={{
										showProjectTreeComment,
										openContextMenu,
									}}
								/>
							) : (
								<div></div>
							)}
						</div>
						<ProjectTreeResizer
							resizableElem={projectTreeRef.current}
							minWidth={SUMMARY_TREE_MIN_WIDTH}
							saveElemWidth={saveSummaryTreeWidth}
						/>
					</div>
				</div>

				<div className="ProjectSummaryTable" ref={scrollBlock}>
					<Routes>
						<Route
							path="/lsr/:id"
							element={
								<LocalEstimateViewer
									setErrorsVisible={setErrorsVisible}
									errorsVisible={errorsVisible}
									scrollBlock={scrollBlock}
									lowerCallbacks={lowerCallbacks}
									parentFetchStatus={localFetchStatus}
									fsnbVisible={fsnbVisible}
									setFsnbVisible={setFsnbVisible}
									onClickShowFsnb={onClickShowFsnb}
								/>
							}
						/>
						<Route path="/osr/:id" element={<ObjectEstimateViewer scrollBlock={scrollBlock} lowerCallbacks={lowerCallbacks} />} />
						<Route path="/" element={<SummaryEstimateViewer scrollBlock={scrollBlock} lowerCallbacks={lowerCallbacks} />} />
						<Route
							path="/kats/:katsId"
							element={
								<KatsViewer
									scrollBlock={scrollBlock}
									lowerCallbacks={lowerCallbacks}
									allColumnsSettings={katsAllColumnsSettings}
									columnsSettings={katsColumnsSettings}
									setColumnsSettings={setKatsColumnsSettings}
									ksrVisible={ksrVisible}
									setKsrVisible={setKsrVisible}
								/>
							}
						/>
						<Route path="/vor/:vorId" element={<VorViewer scrollBlock={scrollBlock} />} />
						<Route path="/vor/:vorId/protocol/" element={<VorViewer scrollBlock={scrollBlock} />} />
						<Route path="/vor" element={<VorViewer scrollBlock={scrollBlock} />} />
						<Route path="/cost_summary/:costSummaryId" element={<CostSummaryViewer scrollBlock={scrollBlock} />} />
						<Route path="/cost_summary/:costSummaryId/protocol/" element={<CostSummaryViewer scrollBlock={scrollBlock} />} />
						<Route path="/cost_summary" element={<CostSummaryViewer scrollBlock={scrollBlock} />} />
						{treeDataHasKatsObjs ? (
							<Route
								path="/kats/"
								element={
									<KatsProjectViewer
										scrollBlock={scrollBlock}
										lowerCallbacks={lowerCallbacks}
										allColumnsSettings={katsAllColumnsSettings}
										columnsSettings={katsColumnsSettings}
										setColumnsSettings={setKatsColumnsSettings}
										ksrVisible={ksrVisible}
										setKsrVisible={setKsrVisible}
									/>
								}
							/>
						) : (
							<Route path="/kats" element={<StartWorkBox />} />
						)}
						<Route path="/start" element={<StartWorkBox />} />
						<Route path="/start/protocol/" element={<StartWorkBox />} />

						<Route
							path="/lsr/:id/protocol"
							element={
								<ProtocolViewer
									objType="lsr"
									scrollBlock={scrollBlock}
									expectPopVisb={expectPopVisb}
									lowerCallbacks={lowerCallbacks}
									setExpectPopVisb={setExpectPopVisb}
								/>
							}
						/>
						<Route
							path="/osr/:id/protocol"
							element={
								<ProtocolViewer
									objType="osr"
									scrollBlock={scrollBlock}
									expectPopVisb={expectPopVisb}
									lowerCallbacks={lowerCallbacks}
									setExpectPopVisb={setExpectPopVisb}
								/>
							}
						/>
						<Route
							path="/kats/:katsId/protocol"
							element={
								<ProtocolViewer
									objType="kats"
									scrollBlock={scrollBlock}
									expectPopVisb={expectPopVisb}
									lowerCallbacks={lowerCallbacks}
									setExpectPopVisb={setExpectPopVisb}
								/>
							}
						/>
						<Route
							path="/protocol"
							element={<ProtocolViewer objType="ssr" scrollBlock={scrollBlock} lowerCallbacks={lowerCallbacks} />}
							expectPopVisb={expectPopVisb}
							setExpectPopVisb={setExpectPopVisb}
						/>
					</Routes>
					{katsJustUploadVisible && (
						<UploadJustModel
							setKatsJustUploadVisible={(val) => {
								setKatsJustUploadVisible(val);
								setIsDraged("leave");
							}}
							justStatusApi={currentPage === "VorViewer" ? apiVor : apiKatsEstimate}
							uploadCallback={uploadJustFiles}
							katsJustUploadCnt={katsJustUploadCnt}
						/>
					)}
					{commentBoxVisible && (
						<CommentsBoxModal
							rowObj={commentBoxObj}
							setVisible={setCommentBoxVisible}
							postActions={{ updateProjectTree: () => dispatch(forceFetchProjectTree(id)) }}
						/>
					)}
					<AlertLoading
						fetchStatus={localFetchStatus}
						func={[setFetchStatus, setFetchSumStatus, setLocalFetchStatus, setKatsFetchStatus]}
						fetchStatusText={fetchStatusText}
						setFetchStatusText={setFetchStatusText}
					/>
				</div>

				{currentPage === "KatsEstimateViewer" && (
					<Footer
						footerItemSettings={KatsViewerFooterSettings({
							katsHeaderData,
							treeWidth: getInitialSummaryTreeWidth(),
							projectMeta,
							userPermissions,
							userSettings,
						})}
					/>
				)}
				{currentPage === "LocalEstimateViewer" && (
					<Footer
						footerItemSettings={LocalEstimateViewerFooterSettings({
							clickedTableCtrlRows,
							lsrData,
							treeWidth: getInitialSummaryTreeWidth(),
							projectMeta,
							userPermissions,
							userSettings,
						})}
					/>
				)}
				{currentPage === "ObjectEstimateViewer" && (
					<Footer
						footerItemSettings={ObjectEstimateViewerFooterSettings({
							clickedTableCtrlRows,
							headerData: objectEstimateHeaderData,
							treeWidth: getInitialSummaryTreeWidth(),
							projectMeta,
							userPermissions,
							userSettings,
						})}
					/>
				)}
				{currentPage === "SummaryEstimateViewer" && (
					<Footer
						footerItemSettings={SummaryEstimateViewerFooterSettings({
							clickedTableCtrlRows,
							headerData: summaryHeaderData,
							treeWidth: getInitialSummaryTreeWidth(),
							projectMeta,
							userPermissions,
							userSettings,
						})}
					/>
				)}
				{!["LocalEstimateViewer", "ObjectEstimateViewer", "KatsEstimateViewer", "SummaryEstimateViewer"].includes(currentPage) && (
					<Footer
						footerItemSettings={GeneralEstmViewerFooterSettings({
							headerData: vorHeaderData,
							treeWidth: getInitialSummaryTreeWidth(),
							projectMeta,
							userPermissions,
							userSettings,
						})}
					/>
				)}

				{katsPriceOfferSelectorVisible && (
					<KatsPriceOfferSelectorModal setVisible={setKatsPriceOfferSelectorVisible} lowerCallbacks={lowerCallbacks} />
				)}

				{confirmVisible === true && <ConfirmDialog />}

				{isContextMenuOpen && (
					<ContextMenu
						items={EstimateViewerContextMenuFactory({
							contextMenuObj,
							callbacks: {
								closeContextMenu: () => dispatch(setIsContextMenuOpen(false)),
								exportProtocolContextMenu: onClickExportCurrentSmetBtn,
								printDocContextMenu: onClickPrintCurrentSmetBtn,
								exportXmlDocContextMenu: onClickCurrentXmlExportBtn,
								deleteDocContextMenu: showDeleteDialog,
								showProjectTreeComment: showProjectTreeComment,
								fillColorTableRow: fillColorTableRow,
								copyEstimateDoc: copyEstimateDoc,
								lowerCallbacks: lowerCallbacks,
								makeLsrDoubleWinner: makeLsrDoubleWinner,
								showDedublicateDialog: showDedublicateDialog,
								applyValidToProjectTreeRow: ({ status }) => dispatch(applyValidToProjectTreeRow({ status, treeRows: selected })),
							},
							currentPage,
							userSettings,
							userPermissions,
							projectTreeSelected: selected,
							projectTree: treeData,
						})}
						isVisible={isContextMenuOpen && getContextMenuVisible(contextMenuObj)}
						position={contextMenuPosition}
						onClose={() => dispatch(setIsContextMenuOpen(false))}
					/>
				)}
				{projectAttrsVisible && <ProjectAttrsModal />}
			</div>
		</>
	);
}
